import { countries as flagCountryCodes } from "country-flag-icons";

/**
 * The canonical country list, so a country is always written the same way. Without this the same
 * market arrives as "Denmark", "denmark", "Danmark" and "DK" depending on who typed it.
 *
 * Neither half is hand-maintained. The codes come from `country-flag-icons`, which we already depend
 * on for the flag SVGs, and the English names come from the browser's own `Intl.DisplayNames`. There
 * is deliberately no name table in this repo to drift out of date.
 *
 * The stored value is the **name**, not the code, so grids, PDFs, the menubar and the backend
 * notification emails all keep reading `countryName` directly with no lookup.
 */

export type CountryOption = {
  /** ISO 3166-1 alpha-2, used for the flag and as the option key. */
  code: string;
  /** Canonical English name. This is what gets stored. */
  name: string;
};

const englishRegionNames = new Intl.DisplayNames(["en"], { type: "region" });

const toOption = (code: string): CountryOption | null => {
  try {
    const name = englishRegionNames.of(code);

    // `of` echoes the code back when it has no name for it (XC, XO). Those are unassigned rather
    // than places anyone trades in, so they are not worth offering.
    return !name || name === code ? null : { code, name };
  } catch {
    // Subdivision codes (GB-SCT, ES-CT, BQ-BO and friends) are in the flag package but are not
    // regions, and Intl throws on them. They are not countries, so they are dropped.
    return null;
  }
};

export const COUNTRY_OPTIONS: CountryOption[] = flagCountryCodes
  .map(toOption)
  .filter((option): option is CountryOption => option !== null)
  .sort((a, b) => a.name.localeCompare(b.name, "en"));

const BY_NAME = new Map(
  COUNTRY_OPTIONS.map((option) => [option.name.toLowerCase(), option])
);

/**
 * Resolves a stored country name back to its option, so an existing record renders with its flag.
 * Matching is case-insensitive to tolerate anything written before the picker existed.
 */
export const findCountryByName = (
  name?: string | null
): CountryOption | null =>
  name ? (BY_NAME.get(name.trim().toLowerCase()) ?? null) : null;
